/* eslint-disable @typescript-eslint/no-explicit-any */
import valid from 'card-validator'
import VisaIcon from '../../assets/icons/svg/VisaIcon.svg'
import MastercardIcon from '../../assets/icons/svg/MastercardIcon.svg'
import AmericanExpressIcon from '../../assets/icons/svg/AmericanExpressIconCard.svg'
import JcbIcon from '../../assets/icons/svg/JCBIcon.svg'
import { lowerCase } from 'lodash'

/**
 * Validate credit card
 *
 * Example cardInfo
 * ```js
 * {
 * 	countryCode: "JP",
 * 	creditCard: {
 * 	  creditCardNumber: '345177925488348',
 * 	  payer: 'test',
 *    creditCardExpires: '2030-10',
 * 	  creditCardSecurityCode: '7373'
 * 	}
 * }
 * ```
 * ---
 * if there are no any error it will return
 * ```js
 * { success: true }
 * ```
 * otherwise
 * ```js
 * { success: false, error_messages: ['some error']}
 * ```
 * ---
 * `countryCode`: must be `TH | JP | AU | NZ`\
 * `creditCardNumber`: is number and no white space\
 * `payer`: a-z, A-Z, 0-9, white space\
 * `creditCardExpires`: format must be like this -> '2030-10'\
 * `creditCardSecurityCode`: number length between 3 - 4
 *
 * @param {object} cardInfo
 * @returns {object}
 */
type TCardInfo = {
    countryCode: string
    creditCard: {
        creditCardNumber: string
        payer: string
        creditCardExpires: string
        creditCardSecurityCode: string
    }
}

function cardValidate(cardInfo: TCardInfo) {
    // mutable object for pass by reference

    const context = {
        cardType: undefined,
        errors: [],
        success: true
    }

    const countryCode = cardInfo.countryCode
    const cardNumber = cardInfo.creditCard.creditCardNumber
    const payer = cardInfo.creditCard.payer
    const expiredDate = cardInfo.creditCard.creditCardExpires
    const cvv = cardInfo.creditCard.creditCardSecurityCode

    validateNumber(context, cardNumber)
    validatePayer(context, payer)
    validateExpirationDate(context, expiredDate)
    validateCountry(context, countryCode)
    validateCVV(context, cvv)
    validateCardType(context)

    if (!context.success) {
        return { success: false, error_messages: context.errors }
    }

    return { success: true, error_messages: [] }
}

function pushError(context: any, errorMsg: string) {
    context.errors.push(errorMsg)
    context.success = false
}

function validateNumber(context: any, cardNumber: string) {
    if (!cardNumber) {
        pushError(context, 'creditcard_not_found')
        return
    }

    const numberValidation = valid.number(cardNumber)
    if (
        !numberValidation.isValid ||
        // not match 0-9
        /^[0-9]*$/g.test(cardNumber) === false
    ) {
        pushError(context, 'creditcard_invalid')
    }

    context.cardType = numberValidation?.card?.type
}

function validatePayer(context: any, payer: string) {
    if (payer === '') {
        pushError(context, 'cardholder_not_found')
        return
    }

    // not match a-z, A-Z, 0-9, whitespace, "." (dot)
    if (/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/g.test(payer) === false) {
        pushError(context, 'cardholder_invalid')
    }
}

function validateExpirationDate(context: any, expiredDate: string) {
    if (expiredDate === undefined) {
        pushError(context, 'expired_not_found')
        return
    }

    const { isValid } = valid.expirationDate(expiredDate)
    if (
        !isValid ||
        // not format like this '2020-30'
        /^\d{2}\/\d{2}$/g.test(expiredDate) === false
    ) {
        pushError(context, 'expired_invalid')
    }
}

function validateCountry(context: any, countryCode: string) {
    if (countryCode === undefined) {
        pushError(context, 'country_code_not_found')
        return
    }

    const isCountrySupport = ['TH', 'JP', 'AU', 'NZ', 'SA', 'SG', 'MY', 'KW'].includes(countryCode)
    if (!isCountrySupport) {
        pushError(context, 'country_code_is_not_support')
    }

    if (!context.cardType) {
        return
    }

    // Todo: allow JCB for TH only
    if (['TH', 'AU', 'NZ'].includes(countryCode)) {
        const isAccept = ['visa', 'mastercard', 'jcb'].includes(context.cardType)
        if (!isAccept) {
            pushError(context, 'only_accept_visa_or_mastercard')
        }
    }

    if (countryCode === 'JP') {
        const acceptList = ['visa', 'mastercard', 'american-express', 'jcb']
        const isAccect = acceptList.includes(context.cardType)
        if (!isAccect) {
            pushError(context, `jp_not_accept_${context.cardType}`)
        }
    }
}

function validateCVV(context: any, cvv: string) {
    if (!cvv) {
        pushError(context, 'securitycode_not_found')
        return
    }

    const cvvLength = context.cardType === 'american-express' ? 4 : 3
    const { isValid } = valid.cvv(cvv, cvvLength)
    if (!isValid) {
        pushError(context, 'securitycode_invalid')
    }
}

function validateCardType(context: any) {
    const isAccept = ['visa', 'mastercard', 'jcb', 'american-express'].includes(context.cardType)
    if (!isAccept) {
        pushError(context, 'only_accept_visa_or_mastercard')
    }
}

function checkCardType(cardNumber: string, full = true) {
    let card = ''
    if (full) {
        // Visa
        const fullVisaRegex = /^4[0-9]{12}(?:[0-9]{3})?$/
        if (fullVisaRegex.test(cardNumber)) {
            card = 'visa'
        }
        // Mastercard
        const fullMastercardRegex = /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/
        if (fullMastercardRegex.test(cardNumber)) {
            card = 'mastercard'
        }

        const validateCardAmex = /^3[47][0-9]{13}$/
        if (validateCardAmex.test(cardNumber)) {
            card = 'american-express'
        } else {
            if (validateJCB(cardNumber)) {
                card = 'jcb'
            }
        }

        return card
    } else {
        // Visa
        const visaRegex = /^4/
        if (visaRegex.test(cardNumber)) {
            card = 'visa'
        }

        // Mastercard
        const mastercardRegex = /^5[1-5]/
        if (mastercardRegex.test(cardNumber)) {
            card = 'mastercard'
        }

        const validateCardAmex = /^3[47][0-9]{2}$/
        if (validateCardAmex.test(cardNumber)) {
            card = 'american-express'
        } else {
            // JCB
            if (validateJCB(cardNumber)) {
                card = 'jcb'
            }
        }

        return card
    }
}

function validateJCB(cardNumber: string) {
    // Regex for JCB format
    const jcbRegex = /^3[0-9]/
    if (!jcbRegex.test(cardNumber)) {
        return false // Invalid format
    }

    return true
}

const BrandOfCard = (cardBrand: string): string => {
    switch (lowerCase(cardBrand)) {
        case 'visa':
            return VisaIcon
        case 'mc':
            return MastercardIcon
        case 'amex':
            return AmericanExpressIcon
        case 'jcb':
            return JcbIcon
        default:
            return ''
    }
}

export const CreditCardHelper = { checkCardType, cardValidate, BrandOfCard }
